import {
  ConditionAction,
  DrawOrderConstants,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
  RegularSpinsSoundModelComponent,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  SoundInstance,
  T_RegularSpinsSoundModelComponent,
} from '@cgs/machines';
import { SpinResponse } from '@cgs/data-access';
import {
  HUD_CONTROLLER_TOKEN,
  TrifectaHudController,
  TrifectaStartPageComponent,
} from '@cgs/provider-trifecta';
import { SCENE_FACTORY_TOKEN, SceneFactory } from '@cgs/common';
import { Button, Compatibility, Container, Platform, SceneObject, T_Platform } from '@cgs/wge';

export const CORSICA_START_PAGE_COMPONENT_TOKEN = Symbol('CORSICA_START_PAGE_COMPONENT_TOKEN');

const enum SceneNames {
  Scene = 'loading/scene',
  LogoScene = 'loading/sceneLogo',
  Logo = 'logo',
  Preloader = 'preloader',
  TimingController = 'timingController',
  LogoOrientation = 'logoOrientation',
  PreloaderMode = 'preloaderMode',
  ContinueButton = 'btn_continue',
}

const enum StateNames {
  Splash = 'splash',
  ZoomIn = 'zoomIn',
  SplashOut = 'splashOut',
  Alpha = 'alpha',
  TimingZoomIn = 'timingController_zoomIn',
  Landscape = 'logoOrientation_landscape',
  Portrait = 'logoOrientation_portrait',
  PreloaderLandscape = 'landscape',
  PreloaderPortrait = 'portrait',
}

const enum HudNodes {
  TrifectaHud = 'trifecta_hud',
  HudController = 'hud_controller',
}

const HUD_SHOW_STATE = 'hud_controller_anim_on';
const SOUND_BUTTON_CLICK = 'button_click';

export class CorsicaStartPageComponent extends TrifectaStartPageComponent {
  private readonly container: Container;
  private readonly sceneFactory: SceneFactory;
  private readonly resources: ResourcesComponent;
  private readonly root: SceneObject;
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;

  private startPageClosed: boolean = false;

  private startPage: SceneObject;
  private hiddenNodes: SceneObject[] = [];

  constructor(container: Container) {
    super();

    this.container = container;
    this.sceneFactory = container.resolve<SceneFactory>(SCENE_FACTORY_TOKEN);
    this.resources = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this.root = this.resources.root;
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.gameStateMachine.init.entered.listen(() => this.show());
    this.gameStateMachine.init.appendLazyAnimation(
      () => new ConditionAction(() => this.startPageClosed)
    );
  }

  // loading.zip is preloaded by the game's CustomLoader before init, so the
  // scenes are already cached here and can be built synchronously — the start
  // page appears in the same frame as init (no slot flash).
  private show(): void {
    // The base builder adds the game node before init, so the slot is already
    // in the tree behind the (partly transparent) loading/scene. Hide the game
    // content while the start page is up, keeping the background visible behind
    // the intro — the old builder achieved this by adding the game node only
    // after the intro finished. Restored in close().
    const bg = this.resources.bg;
    this.hiddenNodes = this.root.childs.filter((child) => child !== bg);
    this.hiddenNodes.forEach((child) => (child.visible = false));

    this.startPage = new SceneObject();
    this.startPage.z = DrawOrderConstants.Top;

    const loadingScene = this.sceneFactory.build(SceneNames.Scene)!;
    loadingScene.initialize();
    this.startPage.addChild(loadingScene);
    this.root.addChild(this.startPage);

    this.playIntro();
  }

  private playIntro(): void {
    const logoScene = this.sceneFactory.build(SceneNames.LogoScene)!;

    const logoOrientation = logoScene.findById<SceneObject>(SceneNames.LogoOrientation);
    const preloaderMode = this.startPage.findById<SceneObject>(SceneNames.PreloaderMode);
    const continueButton = this.startPage.findAllById<Button>(SceneNames.ContinueButton);

    const platform = this.container.resolve<Platform>(T_Platform);

    const buttonClick = this.container
      .resolve<RegularSpinsSoundModelComponent>(T_RegularSpinsSoundModelComponent)
      .regularSpinSoundModel.getSoundByName(SOUND_BUTTON_CLICK);

    platform.view.sizeChanged.listen(() =>
      this.handleOrientationChange(logoOrientation!, preloaderMode!)
    );
    this.handleOrientationChange(logoOrientation!, preloaderMode!);

    if (!continueButton) {
      throw new Error('Continue button not found');
    }

    const preloader = this.startPage.findAllById<SceneObject>(SceneNames.Preloader);
    const logo = logoScene.findAllById<SceneObject>(SceneNames.Logo);
    const timingController = logoScene.findById<SceneObject>(SceneNames.TimingController);

    if (!preloader.length) {
      throw new Error('Preloader not found');
    }

    if (!logo.length) {
      throw new Error('Logo not found');
    }

    if (!timingController) {
      throw new Error('Timing controller not found');
    }

    logoScene.z = 1;
    logoScene.initialize();
    this.startPage.addChild(logoScene);

    preloader.forEach((p) => {
      p.stateMachine!.switchToState(StateNames.Splash);
    });

    const hudController =
      this.container.resolve<TrifectaHudController>(HUD_CONTROLLER_TOKEN);
    if (hudController.isSkipIntro) {
      this.handleSkipIntro(preloader, logo, continueButton, buttonClick);
      return;
    }

    logo.forEach((l) => {
      l.stateMachine!.findById(StateNames.ZoomIn)!.enterAction.done.listen(() => {
        this.close();
      });
    });

    // The camera flies into the frame's hole during timingController_zoomIn;
    // reveal the game right when it finishes so the slot is already visible
    // inside the hole before the logo zoomIn ends (matches the old builder's
    // ready event).
    timingController
      .stateMachine!.findById(StateNames.TimingZoomIn)!
      .enterAction.done.listen(() => {
        this.revealGame();
      });

    preloader.forEach((p) => {
      p.stateMachine!.findById(StateNames.SplashOut)!.enterAction.done.listen(() => {
        this.handleOrientationChange(logoOrientation!, preloaderMode!);

        logo.forEach((l) => {
          l.stateMachine!.switchToState(StateNames.ZoomIn);
        });

        timingController.stateMachine!.switchToState(StateNames.TimingZoomIn);
      });
    });

    continueButton.forEach((button) => {
      const subscribe = button.clicked.listen(() => {
        buttonClick.stop();
        buttonClick.play();

        preloader.forEach((p) => {
          p.stateMachine!.switchToState(StateNames.SplashOut);
        });

        subscribe.cancel();
      });
    });
  }

  private handleSkipIntro(
    preloader: SceneObject[],
    logo: SceneObject[],
    continueButton: Button[],
    buttonClick: SoundInstance
  ): void {
    continueButton.forEach((button) => {
      const subscribe = button.clicked.listen(() => {
        buttonClick.stop();
        buttonClick.play();

        preloader.forEach((p) => {
          p.stateMachine!.switchToState(StateNames.Alpha);
        });

        logo.forEach((l) => {
          l.stateMachine!.switchToState(StateNames.Alpha);
        });

        this.close();

        subscribe.cancel();
      });
    });
  }

  private handleOrientationChange(logoOrientation: SceneObject, preloaderMode: SceneObject): void {
    logoOrientation.stateMachine!.switchToState(
      Compatibility.isPortrait ? StateNames.Portrait : StateNames.Landscape
    );
    preloaderMode.stateMachine!.switchToState(
      Compatibility.isPortrait ? StateNames.PreloaderPortrait : StateNames.PreloaderLandscape
    );
  }

  private close(): void {
    if (this.startPageClosed) {
      return;
    }

    this.startPageClosed = true;
    this.onClose.emit();

    // Reveal the game if the intro was skipped before timingController_zoomIn
    // finished (revealGame is idempotent when already revealed).
    this.revealGame();
    this.revealHud();

    // close() can be called from a state's animation-done callback, which fires
    // inside StateMachine.update() (updateImpl reads this._current.rules right
    // after state.update()). Deinitializing the scene synchronously would null
    // that state mid-update and crash. Defer teardown past the current tick.
    void Promise.resolve().then(() => {
      this.root.removeChild(this.startPage);
      this.startPage.deinitialize();
    });
  }

  private revealGame(): void {
    this.hiddenNodes.forEach((child) => (child.visible = true));
    this.hiddenNodes = [];
  }

  private revealHud(): void {
    const hudController = this.root
      .findById<SceneObject>(HudNodes.TrifectaHud)
      ?.findById<SceneObject>(HudNodes.HudController);
    hudController?.stateMachine?.switchToState(HUD_SHOW_STATE);
  }
}
